import cn from "classnames";
import React from "react";
import Badge from "web-ui/components/internal/Badge";
import Tag from "web-ui/components/internal/Tag";
import { buttonKeyDownHandler } from "web-ui/util/a11y";

import type { VoiceListVoice } from "../../api/generalTts";

import styles from "./VoiceCard.module.scss";
import {
  formatCharacter,
  formatLanguage,
  formatProvider,
  titleCaseFormatter,
} from "./VoiceFinder";

interface Props {
  onSelect?: () => void;
  state?: "enabled" | "selected";
  tabIndex?: number;
  unavailable?: boolean;
  voice: VoiceListVoice;
}

const VoiceCard = ({
  onSelect,
  state = "enabled",
  tabIndex = 0,
  voice,
}: Props) => {
  // TODO: add quality information
  const quality = undefined as "high" | "acceptable" | "low" | undefined;
  return (
    <div
      aria-selected={state === "selected"}
      className={cn(styles.card, {
        [styles.selected]: state === "selected",
        [styles.unavailable]: voice.status === "unavailable",
      })}
      onClick={() => onSelect?.()}
      onKeyDown={buttonKeyDownHandler(() => onSelect?.())}
      tabIndex={tabIndex}
    >
      <div className={styles.header}>
        <span className={styles.name}>{voice.name}</span>
        {quality === "high" ? (
          <Badge className={styles.badge} color="green" size="s">
            High quality
          </Badge>
        ) : quality === "acceptable" ? (
          <Badge className={styles.badge} color="yellow" size="s">
            Acceptable
          </Badge>
        ) : quality === "low" ? (
          <Badge className={styles.badge} color="red" size="s">
            Low quality
          </Badge>
        ) : null}
      </div>
      <div className={styles.footer}>
        <span className={styles.meta}>
          {formatLanguage(
            ...(voice.language_statuses.length
              ? voice.language_statuses.map(status => status.language)
              : [voice.primary_language]),
          )}
        </span>
        <span className={styles.pipe} />
        <span className={styles.meta}>{formatProvider(voice.provider)}</span>
        {voice.age !== undefined ||
        voice.gender !== undefined ||
        voice.world_character !== undefined ? (
          <>
            <span className={styles.pipe} />
            <span className={styles.tags}>
              {voice.world_character === null ? null : (
                <Tag isGhost={true} size="s">
                  {formatCharacter(voice.world_character)}
                </Tag>
              )}
              {voice.gender === null ? null : (
                <Tag isGhost={true} size="s">
                  {titleCaseFormatter(voice.gender)}
                </Tag>
              )}
              {voice.age === null ? null : (
                <Tag isGhost={true} size="s">
                  {titleCaseFormatter(voice.age)}
                </Tag>
              )}
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default VoiceCard;
